// The Venues screen's server state (spec/14 §3.3, F-HQ-04), over the generated client —
// `packages/contracts/src/routes/venues.ts`:
//
//   GET   /v1/hq/venues                                                   every venue, with its flags
//   PATCH /v1/hq/organizations/{organizationId}/venues/{venueId}/listing  set Featured, Partner, Sponsored
//
// Both need `hq:organizations` and a second factor. **Reading the list is audited**: one platform row
// and one row in the log of every organisation on the page, so a page is read when head office asks
// for it and not again on every focus of the window.
//
// The filters live in the page's address (`?city=Pune&status=live`), so a filtered list can be linked
// to — the organisation's page links to its venues — and the back button undoes a filter. An address
// is anyone's to edit, so what it carries is checked here before it reaches the api, which refuses an
// unknown or malformed filter with `400`.

import type { ApiClient, HqVenue, HqVenueList, Venue } from '@clavian/api-client';
import { useInfiniteQuery, type InfiniteData, type QueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

/** A page of the list: the api's maximum. */
const PAGE_SIZE = 100;

export const VENUE_STATUSES = ['setup', 'live', 'paused', 'closed'] as const;
export type VenueStatus = (typeof VENUE_STATUSES)[number];

/** The flags a filter can name; the last three are HQ's to set, the first the organisation's. */
export const LISTING_FILTERS = ['publicListing', 'featured', 'partner', 'sponsored'] as const;
export type ListingFilter = (typeof LISTING_FILTERS)[number];

export const HQ_FLAGS = ['featured', 'partner', 'sponsored'] as const;
export type HqFlag = (typeof HQ_FLAGS)[number];

export interface VenueFilters {
  readonly q?: string;
  readonly city?: string;
  readonly organizationId?: string;
  /** Comma-separated, as the api takes it. */
  readonly status?: string;
  readonly publicListing?: 'true' | 'false';
  readonly featured?: 'true' | 'false';
  readonly partner?: 'true' | 'false';
  readonly sponsored?: 'true' | 'false';
}

/** A TypeID: `org_` and 26 base32 characters. */
const ORGANIZATION_ID = /^org_[0-9a-z]{26}$/;

const text = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
};

/** The filters an address carries, kept only where the api would take them. */
export function venueFiltersFrom(search: Record<string, unknown>): VenueFilters {
  const filters: { -readonly [K in keyof VenueFilters]: VenueFilters[K] } = {};
  const q = text(search['q']);
  if (q !== undefined && q.length >= 2 && q.length <= 120) filters.q = q;
  const city = text(search['city']);
  if (city !== undefined && city.length >= 1 && city.length <= 80) filters.city = city;
  const organizationId = text(search['organizationId'])?.toLowerCase();
  if (organizationId !== undefined && ORGANIZATION_ID.test(organizationId)) filters.organizationId = organizationId;
  const statuses = (text(search['status']) ?? '')
    .split(',')
    .filter((status): status is VenueStatus => (VENUE_STATUSES as readonly string[]).includes(status));
  if (statuses.length > 0) filters.status = VENUE_STATUSES.filter((status) => statuses.includes(status)).join(',');
  for (const flag of LISTING_FILTERS) {
    const value = text(search[flag]);
    if (value === 'true' || value === 'false') filters[flag] = value;
  }
  return filters;
}

export const venueKeys = {
  all: ['hq', 'venues'] as const,
  list: (filters: VenueFilters) => ['hq', 'venues', 'list', filters] as const,
};

export function listVenuesPage(api: ApiClient, filters: VenueFilters, cursor: string | undefined): Promise<HqVenueList> {
  return api.listHqVenues({ query: { limit: PAGE_SIZE, ...filters, ...(cursor === undefined ? {} : { cursor }) } });
}

export function useVenueList(filters: VenueFilters) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: venueKeys.list(filters),
    queryFn: ({ pageParam }) => listVenuesPage(api, filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
    // Every read is audited in each organisation it shows: read when asked, not on a window's focus.
    refetchOnWindowFocus: false,
  });
}

/**
 * A venue as it is now, in every list that shows it — whatever that list's filters. It stays where it
 * is until the list is read again, even if it no longer matches them: a row that vanished the moment
 * its switch was pressed would take the reader's place with it.
 */
export function replaceVenue(queryClient: QueryClient, update: (venue: Venue) => Venue | undefined): void {
  queryClient.setQueriesData<InfiniteData<HqVenueList>>({ queryKey: venueKeys.all }, (data) =>
    data === undefined
      ? data
      : {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            data: page.data.map((row: HqVenue) => {
              const next = update(row.venue);
              return next === undefined ? row : { ...row, venue: next };
            }),
          })),
        },
  );
}
